import type { HTTPError } from 'ky';

import { ApiError, type ApiErrorBody } from './api-error';
import { isBusinessError } from './business-error';

/**
 * Parse the error body from a response.
 * Attempts to parse JSON, falls back to null if parsing fails.
 */
async function parseErrorBody(
    response: Response,
): Promise<ApiErrorBody | null> {
    try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
            const body = await response.clone().json();
            return body as ApiErrorBody;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Transform a ky HTTPError into an ApiError.
 * Used as a beforeError hook in ky.
 */
export async function transformToApiError(error: HTTPError): Promise<ApiError> {
    const { response } = error;
    const body = await parseErrorBody(response);

    const message =
        body?.message ||
        body?.error ||
        error.message ||
        `HTTP ${response.status}: ${response.statusText}`;

    return new ApiError(
        message,
        response.status,
        response.statusText,
        body,
        response,
    );
}

/**
 * Type guard to check if an error is an ApiError.
 */
export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

/**
 * Extract a user-friendly message from any error.
 */
export function getErrorMessage(error: unknown): string {
    if (isApiError(error)) {
        return error.getUserMessage();
    }

    if (isBusinessError(error)) {
        return error.getUserMessage();
    }

    if (error instanceof Error) {
        return error.message;
    }

    return 'An unexpected error occurred.';
}
